import { __decorate, __metadata } from "tslib";
import { LoaderHeader } from '@fluidframework/container-definitions';
import { waitContainerToCatchUp } from '@fluidframework/container-loader';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { deleteFluidCacheIndexDbInstance, FluidCache } from '@fluidframework/driver-web-cache';
import { createOdspUrl } from '@fluidframework/odsp-driver';
import { assertPrecondition, DEFAULT_CONNECTION_TIMEOUT_MS, isMfsDataStore, isMfsVolatileSession, MfsClientEventEmitterBase, MfsHost, Result, } from '@metaos/mfs-client';
import { ActivityEventType, alwaysSendCondition, BEGIN_LOGGING_TEXT, END_LOGGING_TEXT, MfsError, MfsStatusCode, } from '@metaos/mfs-telemetry';
import { buildCachedOdspTokenCallback, buildCachedTokenCallback, storeMfsClientType } from '@metaos/mfs-utility';
import { v4 as uuidv4 } from 'uuid';
import { mfsError } from '../errors/loaderErrors';
import { logActivity, logger, logVerboseDecorator } from '../logger';
import { ErrorCategory, getUserPushChannelInfo } from '../mfs/mfsService';
import { base64Encoder } from '../utils/base64Encoder';
import { loadDataObjects, loadMfsSystemDataObject } from '../utils/dataObjectLoadingUtils';
import { startDataStoreCustomActivities } from '../utils/dataStoreCustomEventHelper';
import { generateOdspCreateContainerRequest, getAllPodFilesWithinFolder, getAppIdHash, getAppsSpecialFolderAsDriveItem, getDriveIdForDomain, getDriveItem, getDriveItemFromDriveItemId, getDriveItemFromSharingURL, getDriveItemFromUrl, getFileVersions, isValidDomain, isValidOdspUrl, restoreFileVersion, } from '../utils/odspUtils';
import { createLoggerContext, setLoggingContext, setLoggingContextFromCallback, setPodLocationIdFromPodId, } from '../utils/setLoggerContext';
import { getOdspContainer } from './getOdspContainer';
/** The default folder path for personal Pods stored in ODB */
const PERSONAL_POD_FOLDER_PATH = 'MFS Data/Personal';
/** Filename for a personal Pod */
const PERSONAL_POD_FILE_NAME = 'default';
/** File extension value for a Pod stored in ODSP */
const POD_FILE_EXTENSION = '.pod';
/** The folder path where shared Pods are stored in SharePoint */
const SHARED_POD_FOLDER_PATH = '.appdata';
/** Max cache age for the Fluid cache. Set to 3 days. */
const MAX_CACHE_AGE = 3 * 24 * 60 * 60 * 1000;
/** Type check if input is OdspStoreLocator */
const isOdspStoreLocator = (input) => input.type === 'odsp';
/** Type guard check that returns true input is with OdspUrlStoreLocator type and false otherwise */
const isOdspUrlStoreLocator = (input) => isOdspStoreLocator(input) && 'url' in input;
/** Type guard check that returns true input is with OdspIdStoreLocator type and false otherwise */
const isOdspIdStoreLocator = (input) => isOdspStoreLocator(input) && 'itemId' in input;
/**
 * Default completion callback, if an error is provided it will log to telemetry and throw the error.
 *
 * @param error Optional error passed when an exception occurs while creating the odsp file.
 */
export const defaultCompletionCallback = (error) => {
    if (error) {
        logger.logError('loadDataStore', error.statusCode, { message: error.message }, 'loadDataStore');
        throw error;
    }
};
/** An MFS client for One Drive and Sharepoint. */
export class OdspClient extends MfsClientEventEmitterBase {
    constructor(config) {
        super();
        this.config = config;
        /**
         * Function that when needed can be passed along as callback parameter to get an ODSP token for specified domain
         *
         * @param domain ODSP domain that token will be used to connect with
         * @returns A `Promise` of access token, whose value is either retrieved from `getOdspTokenCallback` if present in the
         *   config or from MfsService response
         */
        this.getOdspToken = async (domain) => {
            logger.logVerbose('getOdspToken', 'Call to get the access token for ODSP');
            if (this.config.getOdspTokenCallback) {
                return this.config.getOdspTokenCallback(domain);
            }
            throw mfsError.odspTokenException('ODSP token callback not provided');
        };
        /**
         * Function that can be passed along as callback parameter for returning the pushchannel access token.
         *
         * @returns A `Promise` of access token, whose value is either retrieved from `getPushChannelTokenCallback` if present
         *   in client config or from MfsService response
         */
        this.getPushChannelToken = async () => {
            logger.logVerbose('getPushChannelToken', 'Call to get an access token for PushChannel service');
            if (this.config.getPushChannelTokenCallback) {
                return this.config.getPushChannelTokenCallback();
            }
            throw mfsError.pushChannelTokenException('PushChannel token callback not provided');
        };
        /**
         * Function that can be passed along as callback parameter for refreshing ODSP token
         *
         * @param domain ODSP domain that token will be used to connect with
         * @returns A `Promise` of access token, whose value is either retrieved from `getOdspTokenCallback` if present in the
         *   config or from MfsService response
         */
        this.refreshOdspToken = async (domain) => {
            logger.logVerbose('refreshOdspToken', 'Call to get a refreshed access token for ODSP');
            if (this.config.getOdspTokenCallback) {
                return this.config.getOdspTokenCallback(domain);
            }
            throw mfsError.odspTokenException('ODSP token callback not provided');
        };
        storeMfsClientType('odsp');
        this.validateConfiguration();
        this.buildCachedCallbacks(config);
        this.persistedCache = undefined;
        this.loginInfo = this.getUserLoginInfo();
        this.appId = undefined;
        this.userCacheKey = config.userCacheKey;
        this.oneDriveDomain = config.oneDriveDomain;
        this.loginInfo
            .then(async (value) => {
            var _a, _b;
            // Set domain and user cache key only if not previously defined
            (_a = this.oneDriveDomain) !== null && _a !== void 0 ? _a : (this.oneDriveDomain = value.userOneDriveDomain);
            (_b = this.userCacheKey) !== null && _b !== void 0 ? _b : (this.userCacheKey = value.userId);
            const loggerContext = createLoggerContext(value, this.config);
            await this.setLoggerContext(loggerContext);
        })
            .catch((error) => {
            // @todo: (crtargos) Add bootstrap logging.
            // We expect a significant amount of users to not to have a One Drive for Business licence.
            // To avoid incurring a large telemetry cost for those users, we filter those errors and call logVerbose instead.
            if (error.innerError &&
                (error.innerError.message === ErrorCategory.NoValidOneDriveLicense ||
                    error.innerError.message === ErrorCategory.UserOneDriveNotProvisioned)) {
                const errorCategory = error.innerError.message;
                logger.logVerbose('getUserLoginInfo', 'Unable to retrieve push channel info', { errorCategory });
            }
            else {
                logger.logError('SetLoggingContextError', MfsStatusCode.NotFound, { error }, 'getUserLoginInfo');
            }
        });
    }
    /** @inheritdoc */
    async loadDataStore(completionCallback, id, onDataStoreCreated) {
        // Starting readyToRead and readyToWrite activities
        const telemetryActivities = startDataStoreCustomActivities('loadDataStore', ActivityEventType.SINGLE);
        const { dataStore, containerAttachmentCallback } = await this.loadDataObject(2 /* MfsDataStore */, telemetryActivities, completionCallback, onDataStoreCreated, id);
        assertPrecondition(isMfsDataStore(dataStore));
        return { dataStore, containerAttachmentCallback };
    }
    /** @inheritdoc */
    async loadDataStoreAndVolatileSession(id) {
        // Starting readyToRead and readyToWrite activities
        const telemetryActivities = startDataStoreCustomActivities('loadDataStoreAndVolatileSession', ActivityEventType.SINGLE);
        const { dataStore, volatileSession, containerAttachmentCallback } = await this.loadDataObject(2 /* MfsDataStore */ | 4 /* MfsVolatileSession */, telemetryActivities, defaultCompletionCallback, undefined, id);
        assertPrecondition(isMfsDataStore(dataStore));
        assertPrecondition(isMfsVolatileSession(volatileSession));
        return { dataStore, volatileSession, containerAttachmentCallback };
    }
    /** @inheritdoc */
    async loadVolatileSession(id) {
        // Starting readyToRead and readyToWrite activities
        const telemetryActivities = startDataStoreCustomActivities('loadVolatileSession', ActivityEventType.SINGLE);
        const { volatileSession } = await this.loadDataObject(4 /* MfsVolatileSession */, telemetryActivities, defaultCompletionCallback, undefined, id);
        assertPrecondition(isMfsVolatileSession(volatileSession));
        return { volatileSession };
    }
    /** @inheritdoc */
    async loadDataStores(locator, completionCallback, onDataStoreCreated) {
        var _a;
        // Starting readyToRead and readyToWrite activities
        const telemetryActivities = startDataStoreCustomActivities('loadDataStores', ActivityEventType.SINGLE);
        if (!isOdspStoreLocator(locator)) {
            throw mfsError.notOdspStoreLocator();
        }
        const url = isOdspUrlStoreLocator(locator) ? locator.url : `https://${locator.siteDomain}`;
        const isValidUrl = isValidOdspUrl(url);
        if (!isValidUrl) {
            throw mfsError.invalidOdspSite();
        }
        let domain = '';
        let folderPath = '';
        let driveId;
        let itemId;
        if (isOdspUrlStoreLocator(locator)) {
            // When locator is with type of OdspUrlStoreLocator
            const url = new URL(locator.url);
            domain = url.hostname;
            folderPath = url.pathname;
            // extract the driveId, itemId from the URL
            logger.logVerbose('loadDataStores', 'Getting drive item from sharing URL', { locator: 'OdspUrlStoreLocator' });
            const driveAndItemId = await getDriveItemFromSharingURL(domain, locator.url, this.getOdspToken, this.refreshOdspToken);
            driveId = driveAndItemId.driveId;
            itemId = driveAndItemId.itemId;
            logger.logVerbose('loadDataStores', 'Retrieved drive item', { domain, driveId, itemId });
        }
        else if (isOdspIdStoreLocator(locator)) {
            // When locator is with type of OdspIdStoreLocator
            domain = locator.siteDomain;
            driveId = locator.driveId;
            itemId = locator.itemId;
            logger.logVerbose('loadDataStores', 'Getting drive item by id', {
                locator: 'OdspIdStoreLocator',
                driveItemId: itemId,
                driveId,
            });
            const { path, name } = await getDriveItemFromDriveItemId(domain, driveId, itemId, this.getOdspToken, this.refreshOdspToken);
            logger.logVerbose('loadDataStores', 'Retrieved driveItem', { driveItemId: itemId });
            folderPath = `${path}/${name}`;
        }
        else {
            // When locator is with type of OdspLibraryStoreLocator
            domain = locator.siteDomain;
            driveId = locator.driveId;
            folderPath = (_a = locator.partialPath) !== null && _a !== void 0 ? _a : '';
            logger.logVerbose('loadDataStores', '', {
                domain,
                driveId,
                locator: 'OdspLibraryStoreLocator',
            });
        }
        // Load the pod if locator points to a Pod file
        if (folderPath.endsWith(POD_FILE_EXTENSION)) {
            logger.logVerbose('loadDataStores', `Locator folderPath points to a pod`);
            if (!driveId || !itemId) {
                const driveItem = await getDriveItem(domain, folderPath, this.getOdspToken, this.refreshOdspToken, driveId);
                driveId = driveItem.driveId;
                itemId = driveItem.itemId;
                logger.logVerbose('loadDataStores', 'Retrieved item', { driveId, driveItemId: itemId });
            }
            const { dataStore } = await this.mountPodById(domain, driveId, 2 /* MfsDataStore */, itemId, telemetryActivities, completionCallback, onDataStoreCreated);
            assertPrecondition(isMfsDataStore(dataStore));
            return [{ dataStore }];
        }
        // Get all Pods under `/.appdata` folder from folder path location of specified drive
        const pods = await getAllPodFilesWithinFolder(domain, this.concatSharedPodPath(folderPath), this.getOdspToken, this.refreshOdspToken, driveId);
        const dataStores = [];
        for (const item of pods) {
            try {
                const { dataStore } = await this.mountPodById(domain, item.driveId, 2 /* MfsDataStore */, item.itemId, telemetryActivities, completionCallback, onDataStoreCreated);
                assertPrecondition(isMfsDataStore(dataStore));
                dataStores.push({ dataStore });
            }
            catch (e) {
                if (!e.statusCode || e.statusCode != MfsStatusCode.NotAllowed) {
                    throw e;
                }
            }
        }
        return dataStores;
    }
    /**
     * Creates and load the Mfs data store at specified location
     *
     * @param locator The ODSP store locator used to identify Pod location. The Pod file will be created inside `.appdata`
     *   folder located under provided location. Error will be thrown if store locator is not pointing to an ODSP folder.
     * @returns A `Promise` of MfsDataStore object connected to Fluid
     */
    async createDataStore(locator, completionCallback, onDataStoreCreated) {
        var _a;
        // Starting readyToRead and readyToWrite activities
        const telemetryActivities = startDataStoreCustomActivities('createDataStore', ActivityEventType.SINGLE);
        if (!isOdspStoreLocator(locator)) {
            throw mfsError.notOdspStoreLocator();
        }
        const url = isOdspUrlStoreLocator(locator) ? locator.url : `https://${locator.siteDomain}`;
        const isValidUrl = isValidOdspUrl(url);
        if (!isValidUrl) {
            throw mfsError.invalidOdspSite();
        }
        let folderPathUrl = '';
        let driveId;
        let podFileName;
        if (isOdspUrlStoreLocator(locator)) {
            logger.logVerbose('createDataStore', 'Locator is of type OdspUrlStoreLocator');
            // When locator is with type of OdspUrlStoreLocator
            const url = new URL(locator.url);
            const domain = url.hostname;
            folderPathUrl = locator.url;
            // extract the driveId from the URL
            const driveAndItemId = await getDriveItemFromSharingURL(domain, folderPathUrl, this.getOdspToken, this.refreshOdspToken);
            driveId = driveAndItemId.driveId;
        }
        else if (isOdspIdStoreLocator(locator)) {
            logger.logVerbose('createDataStore', '', { locator: 'OdspIdStoreLocator' });
            // When locator is with type of OdspIdStoreLocator
            const { isFolder, path, name } = await getDriveItemFromDriveItemId(locator.siteDomain, locator.driveId, locator.itemId, this.getOdspToken, this.refreshOdspToken);
            if (!isFolder && name.endsWith(POD_FILE_EXTENSION)) {
                logger.logVerbose('createDataStore', 'Mounting pod', {
                    driveId: locator.driveId,
                    driveItemId: locator.itemId,
                });
                const { dataStore, containerAttachmentCallback } = await this.mountPodById(locator.siteDomain, locator.driveId, 2 /* MfsDataStore */, locator.itemId, telemetryActivities, completionCallback, onDataStoreCreated);
                assertPrecondition(isMfsDataStore(dataStore));
                logger.logVerbose('createDataStore', 'Data store loaded from mounted pod', {
                    dataStoreId: dataStore.getId(),
                });
                return { dataStore, containerAttachmentCallback };
            }
            folderPathUrl = `https://${locator.siteDomain}${path}/${name}`;
            driveId = locator.driveId;
        }
        else {
            logger.logVerbose('createDataStore', '', { locator: 'OdspLibraryStoreLocator', domain: locator.siteDomain });
            // When locator is with type of OdspLibraryStoreLocator
            folderPathUrl = `https://${locator.siteDomain}/${((_a = locator.partialPath) !== null && _a !== void 0 ? _a : '').replace(/^\/+/, '')}`;
            driveId = locator.driveId;
        }
        // When pointing to a Pod file, create the file with predefined name at target location not `/.appdata` subfolder
        if (folderPathUrl.endsWith(POD_FILE_EXTENSION)) {
            const params = folderPathUrl.split('/');
            podFileName = params.pop();
            folderPathUrl = params.join('/');
        }
        else {
            folderPathUrl = this.concatSharedPodPath(folderPathUrl);
        }
        logger.logVerbose('createDataStore', 'Creating pod file at url', { driveId, folderPath: folderPathUrl });
        const { dataStore, containerAttachmentCallback } = await this.createPodFile(folderPathUrl, 2 /* MfsDataStore */, telemetryActivities, completionCallback, onDataStoreCreated, driveId, podFileName);
        assertPrecondition(isMfsDataStore(dataStore));
        logger.logVerbose('createDataStore', 'Data store loaded from created pod', {
            dataStoreId: dataStore.getId(),
        });
        return { dataStore, containerAttachmentCallback };
    }
    /** @inheritdoc */
    async unloadDataStore(id) {
        // @todo: (vanessazhao) Investigate how to unload a Fluid object and add API support, ref:https://office.visualstudio.com/MetaOS/_workitems/edit/5516110
        throw mfsError.notImplemented();
    }
    /** Helper function to load specific data object */
    async loadDataObject(dataObjectsToLoad, activities, completionCallback, onDataStoreCreated, id) {
        var _a;
        let siteDomain, driveId, itemId;
        if (id) {
            logger.logVerbose('loadDataObject', 'Locating pod file', { podId: id });
            ({ siteDomain, driveId, itemId } = await this.getLocatorFromPodId(id));
        }
        else {
            logger.logVerbose('loadDataObject', 'Getting user one drive domain');
            siteDomain = (_a = this.oneDriveDomain) !== null && _a !== void 0 ? _a : (await this.loginInfo).userOneDriveDomain;
            if (!siteDomain) {
                throw mfsError.oneDriveDomainException();
            }
            const defaultPodName = await this.getDefaultPodName();
            try {
                ({ driveId, itemId } = await this.getDefaultPodDriveItem(siteDomain, defaultPodName));
            }
            catch (e) {
                if (e instanceof MfsError && e.statusCode === 404) {
                    return this.createDefaultPod(dataObjectsToLoad, activities, siteDomain, defaultPodName, completionCallback, onDataStoreCreated);
                }
                else {
                    throw e;
                }
            }
        }
        return this.mountPodById(siteDomain, driveId, dataObjectsToLoad, itemId, activities, completionCallback, onDataStoreCreated);
    }
    async createDefaultPod(dataObjectsToLoad, activities, domain, defaultPodName, completionCallback, onDataStoreCreated) {
        const { name } = await getAppsSpecialFolderAsDriveItem(domain, this.getOdspToken, this.refreshOdspToken);
        const fileFolderPathUrl = `https://${domain}/${name}/${PERSONAL_POD_FOLDER_PATH}`;
        return this.createPodFile(fileFolderPathUrl, dataObjectsToLoad, activities, completionCallback, onDataStoreCreated, undefined, defaultPodName);
    }
    async getDefaultPodName() {
        const appId = await this.getAppId();
        const appIdHash = getAppIdHash(appId);
        const defaultPodName = `${PERSONAL_POD_FILE_NAME}${appIdHash}${POD_FILE_EXTENSION}`;
        logger.logVerbose('getDefaultPodName', 'Returns', { appIdHash, defaultPodName });
        return defaultPodName;
    }
    async getDefaultPodDriveItem(domain, defaultPodName) {
        const url = `https://${domain}/_api/v2.1/drive/special/apps:/${PERSONAL_POD_FOLDER_PATH}/${defaultPodName}?select=id,parentReference/driveId`;
        return getDriveItemFromUrl(domain, url, this.getOdspToken, this.refreshOdspToken);
    }
    /** Recovers pod file to the latest healthy version */
    async recoverPodFile(domain, driveId, itemId, podId) {
        this.emitMfsClientEvent('recoveryStart', { podId });
        let lastError = null;
        const fileVersionsResult = await getFileVersions(domain, driveId, itemId, this.getOdspToken, this.refreshOdspToken);
        if (fileVersionsResult.isOK === true) {
            const lastHealthyVersionResult = await this.findLastHealthyPodfile(fileVersionsResult.value, domain, driveId, itemId);
            if (lastHealthyVersionResult.isOK === true) {
                const restoreFileVersionResult = await restoreFileVersion(domain, driveId, itemId, lastHealthyVersionResult.value, this.getOdspToken, this.refreshOdspToken);
                if (restoreFileVersionResult.isOK === false) {
                    lastError = restoreFileVersionResult.error;
                }
                logger.logVerbose('recoverPodFile', 'File successfully restored');
            }
            else {
                lastError = lastHealthyVersionResult.error;
            }
        }
        else {
            lastError = fileVersionsResult.error;
        }
        this.emitMfsClientEvent('recoveryEnd', { podId, successfulRecovery: lastError === null });
        if (lastError) {
            throw lastError;
        }
    }
    /**
     * Finds the last healthy pod file. Used during the pod corruption recovery process to determine what file version to
     * recover to.
     *
     * @param fileVersions List of file versions sorted by modification time in descending order
     * @param domain ODSP user domain
     * @param driveId Pod file drive id
     * @param itemId Pod file item id
     * @returns A `Promise` of Result<OdspFileVersion> object
     */
    async findLastHealthyPodfile(fileVersions, domain, driveId, itemId) {
        if (!fileVersions) {
            return Result.fail(new Error('Invalid OdspFileVersion array'));
        }
        const siteUrl = `https://${domain}`;
        let lastHealthyVersion = null;
        // Load each version in ODSP backups starting from the latest, stopping when we find the first healthy pod
        for (const version of fileVersions) {
            const containerUrl = createOdspUrl({
                siteUrl: siteUrl,
                driveId,
                itemId,
                dataStorePath: '',
                fileVersion: version.id,
            });
            try {
                logger.logVerbose('findLastHealthyPodfile', 'Loading pod file', { versionId: version.id });
                // Load container
                const container = await getOdspContainer(containerUrl, domain, this.getOdspToken, this.getPushChannelToken, this.persistedCache, { [LoaderHeader.loadMode]: { opsBeforeReturn: 'cached' } });
                // Unload container
                container.close();
                lastHealthyVersion = version;
                logger.logVerbose('findLastHealthyPodfile', 'last Healthy Version', { lastHealthyVersion });
                break;
            }
            catch (e) {
                // Continue looping only while we get corruption errors.
                if (!this.isPodFileCorruptionError(e)) {
                    return e instanceof Error ? Result.fail(e) : Result.fail(new Error('Error finding last healthy pod'));
                }
                logger.logVerbose('findLastHealthyPodfile', 'Corrupted pod file', { versionId: version.id });
            }
        }
        return lastHealthyVersion ? Result.ok(lastHealthyVersion) : Result.fail(new Error('Failure finding a healthy pod'));
    }
    /** User-defined type guard to determine if input has innerErrorCode */
    isDriverErrorBase(input) {
        // make sure that input is not a primitive type
        if (typeof input === 'object' && input !== null) {
            return 'errorType' in input && 'message' in input && 'canRetry' in input;
        }
        return false;
    }
    /** Function that will mount the specified Pod using its Id. If the Pod does not exist, an exception will be thrown. */
    async mountPodById(domain, driveId, dataObjectsToLoad, itemId, activities, completionCallback, onDataStoreCreated) {
        logger.logVerbose('mountPodById', 'Load the data store using specified Pod ID');
        const containerUrl = createOdspUrl({
            siteUrl: `https://${domain}`,
            driveId: driveId,
            itemId: itemId,
            dataStorePath: '',
        });
        return this.loadPod(containerUrl, domain, driveId, dataObjectsToLoad, activities, false, completionCallback, onDataStoreCreated, itemId, undefined);
    }
    /** @inheritdoc */
    async clearCache() {
        var _a;
        (_a = this.config.localStore) === null || _a === void 0 ? void 0 : _a.clear();
        if (!this.persistedCache) {
            return;
        }
        // @todo: (crtargos) Currently there isn't a way with IPersistedCache to delete individual user cache entries. Deleting the whole indexedDB database instead.
        return deleteFluidCacheIndexDbInstance();
    }
    /**
     * Function that will create a Pod at the path provided if the user has access. If the path does not exist, the user
     * does not have access, or the Pod already exists an exception will be thrown.
     */
    async createPodFile(pathFolderUrl, dataObjectsToLoad, activities, completionCallback, onDataStoreCreated, driveId, fileName) {
        logger.logVerbose('createPodFile', 'Enter', { dataObjectsToLoad, driveId });
        const url = new URL(pathFolderUrl);
        const domain = url.hostname;
        const folderPath = url.pathname;
        // @todo: (vanessazhao) Keep retrying with different filenames if it is already exists. DriverErrorType doesn't throw FileExists error, instead it will create the file with index number appended(e.g. <uuid> 1.pod). Need to catch this edge case and get correct the file itemId.
        logger.logVerbose('createPodFile', `${BEGIN_LOGGING_TEXT}: getAppId`);
        const appId = await this.getAppId();
        logger.logVerbose('createPodFile', `${END_LOGGING_TEXT}: getAppId`, { appId });
        const appIdHash = getAppIdHash(appId);
        const podName = fileName !== null && fileName !== void 0 ? fileName : `${uuidv4()}${appIdHash}${POD_FILE_EXTENSION}`;
        if (!driveId) {
            logger.logVerbose('createPodFile', `${BEGIN_LOGGING_TEXT}: getDriveIdForDomain`);
            driveId = await getDriveIdForDomain(domain, this.getOdspToken, this.refreshOdspToken);
            logger.logVerbose('createPodFile', `${END_LOGGING_TEXT}: getDriveIdForDomain`, { driveId });
        }
        const containerRequest = generateOdspCreateContainerRequest(url, podName, driveId);
        return this.loadPod(containerRequest, domain, driveId, dataObjectsToLoad, activities, true, completionCallback, onDataStoreCreated, undefined, `${folderPath}/${podName}`);
    }
    /** Load the specified container into a MfsDataStore and return the MfsDataStore object */
    async loadPod(containerConnection, domain, driveId, dataObjectsToLoad, activities, isCreating, completionCallback, onDataStoreCreated, itemId, filePath) {
        var _a;
        if (!this.config.disableCache && !this.persistedCache) {
            //Cache partition key is the userId
            const fluidCacheConfig = {
                partitionKey: (_a = this.userCacheKey) !== null && _a !== void 0 ? _a : (await this.loginInfo).userId,
                maxCacheItemAge: MAX_CACHE_AGE,
            };
            this.persistedCache = new FluidCache(fluidCacheConfig);
        }
        let container;
        try {
            container = await getOdspContainer(containerConnection, domain, this.getOdspToken, this.getPushChannelToken, this.persistedCache, { [LoaderHeader.loadMode]: { opsBeforeReturn: 'cached', deltaConnection: 'delayed' } });
        }
        catch (e) {
            if (this.isPodFileCorruptionError(e) && itemId) {
                const podId = await this.toPodId(domain, driveId, itemId);
                const loggerParams = { podId };
                // Recover pod file asynchronously
                this.recoverPodFile(domain, driveId, itemId, podId)
                    .then(() => {
                    logger.logInfo('podRecoverySuccess', `Pod recovered successfully`, 'podRecovery', loggerParams);
                })
                    .catch((e) => {
                    logger.logError('podRecoveryFailed', MfsStatusCode.DataCorruption, { message: `Pod recovery failed`, error: e }, 'podRecovery', loggerParams);
                });
                throw mfsError.corruptedPodFileError();
            }
            else {
                throw e;
            }
        }
        let podId;
        const appId = await this.getAppId();
        const mfsSystemDataObject = await loadMfsSystemDataObject(container);
        if (isCreating) {
            logger.logVerbose('loadPod', 'Setting appId', { appId });
            mfsSystemDataObject.setAppId(appId);
            podId = 'detachedPodId';
        }
        else {
            [podId, itemId] = await this.getPodIdFromODSP(domain, driveId, itemId, filePath);
            logger.logInfo('LoadPod', 'Load Pod for User', 'LoadPod', { podId, driveId, itemId });
            const podAppId = mfsSystemDataObject.getAppId();
            if (podAppId && podAppId != appId) {
                throw mfsError.appNotAllowedToAccessPod(appId, podAppId);
            }
            [podId, itemId] = await this.getPodIdFromODSP(domain, driveId, itemId, filePath);
            setPodLocationIdFromPodId(podId);
        }
        const mfsApp = new MfsHost(appId, this.config.hostConfig);
        let result;
        try {
            result = await loadDataObjects(dataObjectsToLoad, container, mfsApp, podId);
            if (!result) {
                throw new MfsError('Failed to load data objects, loadDataObjects returned undefined', MfsStatusCode.UnhandledException);
            }
            if (!isCreating) {
                this.emitMfsClientEvent('readyToRead', { podId });
                logger.endActivitySuccess(activities.readyToRead, alwaysSendCondition);
            }
            if (!isMfsDataStore(result.dataStore))
                throw new MfsError('Load data objects did not return a MfsDataStore', MfsStatusCode.UnhandledException);
            const datastore = result.dataStore;
            if (isCreating && typeof containerConnection !== 'string') {
                // We now will take the opportunity to load the application layers boot items, now that we have access to the mfsDataStore. We surround the onDataStoreCreated Callback with a try and empty catch block as the application layer is responsible for handling these errors and the MfsDataStore should still load.
                if (onDataStoreCreated) {
                    try {
                        await onDataStoreCreated(datastore);
                        // eslint-disable-next-line no-empty
                    }
                    catch (e) { }
                }
                const callback = async () => container
                    .attach(containerConnection)
                    .then(async () => {
                    [podId, itemId] = await this.getPodIdFromODSP(domain, driveId, itemId, filePath);
                    datastore.setId(podId);
                    setPodLocationIdFromPodId(podId);
                    await this.waitForUpdates(container);
                    // At this stage in the create path, we have completed attaching the odsp container
                    // We can now start the idle tracker and call the completion callback.
                    this.trackContainerForIdleDisconnects(container);
                    this.attemptCompletionCallback(completionCallback);
                    logger.logInfo('LoadPod', 'Load Pod for User', 'LoadPod', { podId: podId });
                    logger.logVerbose('loadPod', 'Detached Pod has been attached successfully');
                })
                    .catch((error) => {
                    logger.logVerbose('loadPod', 'Detached Pod failed to attach');
                    this.attemptCompletionCallback(completionCallback, error);
                });
                result.containerAttachmentCallback = callback;
            }
        }
        catch (e) {
            if (!isCreating) {
                logger.endActivityFailure(activities.readyToRead, e);
            }
            throw e;
        }
        this.attachEventListeners(container, DEFAULT_CONNECTION_TIMEOUT_MS);
        logger.logVerbose('loadPod', 'Waiting for Fluid container to catch up...');
        if (!isCreating) {
            // Await for pod updates now that we have the podID
            logger.logVerbose('loadPod', 'Waiting for updates from pod', { podId });
            await this.waitForUpdates(container, podId, activities);
            if (podId && activities) {
                this.emitReadyToWriteEvent(podId, activities);
            }
            this.trackContainerForIdleDisconnects(container);
            this.attemptCompletionCallback(completionCallback);
        }
        return result;
    }
    /**
     * Track container for idle disconnections. The container will be disconnected and reconnected based on user activity.
     *
     * @param container Container to track
     */
    trackContainerForIdleDisconnects(container) {
        var _a;
        (_a = this.config.idleTracker) === null || _a === void 0 ? void 0 : _a.then((idleTracker) => {
            idleTracker.track(container);
        }).catch((unknownError) => {
            const error = unknownError instanceof Error ? unknownError : undefined;
            logger.logError('containerTrackingError', MfsStatusCode.ExpectationFailed, { message: 'Error while idle-tracking container', error }, 'trackContainerForIdleDisconnects');
        });
    }
    /**
     * Helper function to set the loggerContext
     *
     * @param loggerContext LoggerContext object that we want to set
     */
    async setLoggerContext(loggerContext) {
        try {
            if (this.config.sinkCallback) {
                await setLoggingContextFromCallback(loggerContext, this.config.sinkCallback);
                return;
            }
        }
        catch (error) {
            let sinkCallbackErrorInfo = {
                message: 'sinkCallback threw an error. Will set Websink using setLoggingContext or sink param',
            };
            if (error instanceof Error) {
                sinkCallbackErrorInfo = { ...sinkCallbackErrorInfo, error };
            }
            logger.logError('SetLoggingContextError', MfsStatusCode.BadRequest, sinkCallbackErrorInfo, 'setLoggingContextFromCallback');
        }
        this.config.setLoggingContext
            ? this.config.setLoggingContext(loggerContext, this.config.sink)
            : setLoggingContext(loggerContext, this.config.sink);
    }
    /**
     * Attempts to preform the completionCallback provided which could produce an error. We wrap the callback in an empty
     * try catch as the application layer is responsible for handling these errors. If an error occurs the MfsDataStore
     * should still be loaded.
     */
    attemptCompletionCallback(completionCallback, error) {
        try {
            completionCallback(error);
            // eslint-disable-next-line no-empty
        }
        catch (e) { }
    }
    /** Gets the podId by making a call to odsp if the itemId is undefined. Returns a tuple containing the [podId, itemId] */
    async getPodIdFromODSP(domain, driveId, itemId, filePath) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            if (!itemId) {
                const driveItem = await getDriveItem(domain, filePath !== null && filePath !== void 0 ? filePath : '', this.getOdspToken, this.refreshOdspToken, driveId);
                itemId = driveItem.itemId;
            }
            const podId = await this.toPodId(domain, driveId, itemId);
            logger.logVerbose('getPodIdFromODSP', '', { podId, itemId });
            resolve([podId, itemId]);
        });
    }
    /** Waits for the fluidContainer to catch up. */
    async waitForUpdates(container, podId, activities) {
        if (this.config.waitForUpdates) {
            try {
                await waitContainerToCatchUp(container);
            }
            catch (error) {
                if (activities) {
                    logger.endActivityFailure(activities.readyToWrite, error);
                }
                throw mfsError.containerCatchUpException(error instanceof Error ? error.message : undefined);
            }
        }
        else {
            waitContainerToCatchUp(container).catch((error) => {
                if (activities) {
                    logger.endActivityFailure(activities.readyToWrite, error);
                }
                throw mfsError.containerCatchUpException(error instanceof Error ? error.message : undefined);
            });
        }
    }
    /** Utility method to emit a readyToWrite event */
    emitReadyToWriteEvent(podId, activities) {
        this.emitMfsClientEvent('readyToWrite', { podId });
        logger.endActivitySuccess(activities.readyToWrite, alwaysSendCondition);
        logger.logInfo('fluidContainerReady', 'Fluid container is ready to write.', 'fluidContainerReady');
        logger.logVerbose('emitReadyToWriteEvent', 'Emitted ready to write', { podId });
    }
    /** Determines if an error is caused by pod file corruption */
    isPodFileCorruptionError(input) {
        return (this.isDriverErrorBase(input) &&
            input.errorType === DriverErrorType.genericNetworkError &&
            'innerMostErrorCode' in input &&
            (input === null || input === void 0 ? void 0 : input.innerMostErrorCode) === 'fluidInvalidSchema');
    }
    /** Call MfsService to get the user PushChannel info for login */
    async getUserLoginInfo() {
        // set the skipOneDriveDomain indicator from Mfs Service to true if OneDrive Domain is passed into SDK, else set to false
        const skipOneDriveDomain = this.config.oneDriveDomain ? true : false;
        return getUserPushChannelInfo(this.config.getAccessTokenCallback, this.config.sessionId, skipOneDriveDomain, this.config.mfsServiceDomain).then((serviceResponse) => {
            var _a, _b;
            if (serviceResponse.success === false || !serviceResponse.pushChannelInfo) {
                const innerError = new Error((_a = serviceResponse.error) === null || _a === void 0 ? void 0 : _a.errorCategory);
                throw mfsError.getUserPushChannelInfoException((_b = serviceResponse.error) === null || _b === void 0 ? void 0 : _b.message, innerError);
            }
            return serviceResponse.pushChannelInfo;
        });
    }
    /**
     * Add the Shared Pod Folder Path to the provided folder path if it isn't already included.
     *
     * @param folderPath Path to the folder where teh Pod is located.
     * @returns The folder path with .appdata concatenated if needed.
     */
    concatSharedPodPath(folderPath) {
        // Remove any leading and trailing slashes.
        const folder = folderPath.replace(/^\/+|\/+$/g, '');
        if (folder.endsWith(SHARED_POD_FOLDER_PATH)) {
            logger.logVerbose('concatSharedPodPath', '', { folderPath: SHARED_POD_FOLDER_PATH });
            return folder;
        }
        const sharedPodPath = folder.split('/').length > 1 || folder.length > 0
            ? folder.concat(`/${SHARED_POD_FOLDER_PATH}`)
            : folder.concat(`${SHARED_POD_FOLDER_PATH}`);
        return sharedPodPath;
    }
    /** Helper function to generate the Pod ID for ODSP-based Pod */
    async toPodId(domain, driveId, itemId) {
        const appId = await this.getAppId();
        return base64Encoder.btoa(`ODSP|${domain.toLowerCase()}|${driveId}|${itemId}|${appId}`);
    }
    /** Helper function to get domain, driveId and itemId from an ODSP Pod ID */
    async getLocatorFromPodId(id) {
        const decodedId = base64Encoder.atob(id);
        if (!decodedId.startsWith('ODSP')) {
            throw mfsError.invalidPodId();
        }
        const params = decodedId.split('|');
        const appId = await this.getAppId();
        const decodedAppId = params[4];
        if (appId !== decodedAppId) {
            throw mfsError.podFileNameHasIncorrectSuffix();
        }
        return { siteDomain: params[1], driveId: params[2], itemId: params[3] };
    }
    /** Helper function to get user's app ID */
    async getAppId() {
        if (!this.appId) {
            this.appId = (await this.loginInfo).applicationId;
        }
        logger.logVerbose('getAppId', '', { appId: this.appId });
        return this.appId;
    }
    /** Helper function that modifies the callbacks received on the constructor to store the session storage data */
    buildCachedCallbacks(config) {
        this.config.getOdspTokenCallback = buildCachedOdspTokenCallback(config.getOdspTokenCallback);
        this.config.getAccessTokenCallback = buildCachedTokenCallback('access', config.getAccessTokenCallback);
        this.config.getPushChannelTokenCallback = config.getPushChannelTokenCallback
            ? buildCachedTokenCallback('push', config.getPushChannelTokenCallback)
            : config.getPushChannelTokenCallback;
    }
    /** Validates OdspClientConfig. Throws an error if the configuration is invalid. */
    validateConfiguration() {
        // Only validate the domain if it was passed. If no domain was passed, we'll get it from MFS Service.
        if (this.config.oneDriveDomain && !isValidDomain(this.config.oneDriveDomain)) {
            const invalidDomainError = mfsError.invalidDomain(this.config.oneDriveDomain);
            logger.logError('validateConfiguration', MfsStatusCode.BadRequest, { error: invalidDomainError }, 'domainValidation', { domain: this.config.oneDriveDomain });
            throw invalidDomainError;
        }
    }
}
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Function, String, Function]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadDataStore", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadDataStoreAndVolatileSession", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadVolatileSession", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Function, Function]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadDataStores", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Function, Function]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "createDataStore", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "unloadDataStore", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Object, Function, Function, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadDataObject", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Object, String, String, Function, Function]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "createDefaultPod", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getDefaultPodName", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getDefaultPodDriveItem", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "recoverPodFile", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "findLastHealthyPodfile", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Boolean)
], OdspClient.prototype, "isDriverErrorBase", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String, Number, String, Object, Function, Function]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "mountPodById", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "clearCache", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, Number, Object, Function, Function, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "createPodFile", null);
__decorate([
    logActivity(ActivityEventType.NONE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, String, Number, Object, Boolean, Function, Function, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "loadPod", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], OdspClient.prototype, "trackContainerForIdleDisconnects", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "setLoggerContext", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Function, MfsError]),
    __metadata("design:returntype", void 0)
], OdspClient.prototype, "attemptCompletionCallback", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getPodIdFromODSP", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "waitForUpdates", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, Object]),
    __metadata("design:returntype", void 0)
], OdspClient.prototype, "emitReadyToWriteEvent", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Boolean)
], OdspClient.prototype, "isPodFileCorruptionError", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getUserLoginInfo", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", String)
], OdspClient.prototype, "concatSharedPodPath", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String, String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "toPodId", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getLocatorFromPodId", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OdspClient.prototype, "getAppId", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], OdspClient.prototype, "buildCachedCallbacks", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], OdspClient.prototype, "validateConfiguration", null);
//# sourceMappingURL=odspClient.js.map